import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";
import { accesRefletField } from "./acces.js";
import { mentionsSchema } from "./mentions.js";

/**
 * Message : famille "communication individuelle".
 *
 * Communication individuelle envoyée ou reçue, tous canaux confondus (email,
 * sms, notification, portail, note d'appel), avec ses destinataires et son
 * statut de remise. Construite sur le modèle "famille + type" de la bible
 * (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du message ("rappel de
 * rendez-vous", "confirmation de commande", "réponse support") : elle est
 * portée par le discriminant `type` (chaîne libre déclarée par l'app). Le canal
 * (`channel`), le sens (`direction`) et l'état de remise (`deliveryStatus`) sont
 * des états métier À PART, distincts du discriminant (même logique que `order`
 * qui porte type + direction + status).
 *
 * Références vers d'autres familles = de simples ids (aucun import d'un autre
 * schéma) :
 *   - `fromRef` (émetteur), `toRefs` (destinataires) et `threadRef` (fil de
 *     conversation) sont volontairement polymorphes : l'émetteur ou un
 *     destinataire peut être un `contact`, un `worker` ou l'org, donc de simples
 *     ids que l'app résout selon le contexte (pas un `xxxId` typé sur une seule
 *     famille) ;
 *   - les liens de CONTEXTE (contactId, bookingId, invoiceId, ticketId,
 *     campaignId, reviewId) disent "de quoi parle ce message" et restent
 *     optionnels.
 *
 * Acces : la famille est HERITIERE d'un acces par fiche (`acces.ts`,
 * `docs/socle/acces-par-fiche.md`). Un message qui porte `conversationId`
 * herite des droits de sa conversation (ses participants, ou la fiche ancree)
 * et porte en REFLET `acces: { visibilite }`, recopie de la conversation par
 * le SDK et verifie par les rules. Sans `conversationId`, un message est
 * `equipe` : la porte decide, comme avant.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Le `type` d'un message de conversation (canal, direct, chatter) : contrat conversations du 05/10/2026. */
export const MESSAGE_TYPE_CONVERSATION = "conversation";
export const MESSAGE_TEXTE_MAX = 20_000;
/** Les auteurs RESERVES aux Functions : le client ecrit son propre personId. */
export const AUTEUR_LIA = "lia";
export const AUTEUR_SYSTEME = "systeme";

/**
 * LES REACTIONS d'un message de conversation : une liste FERMEE de trois
 * emojis, chacun range sous une CLE courte (`pouce`, `pouceBas`, `fete`), et pour
 * chaque cle la liste des personIds qui ont reagi. La cle, pas l'emoji, est le
 * nom du champ : les rules la nomment une par une (elles ne savent pas boucler
 * sur des cles inconnues), et un emoji n'est pas un nom de champ sur.
 * Recopie dans `@taki/ui` (`conversations/reactions.ts`) et dans les rules ;
 * un test compare (`packages/sdk/tests/conversations-contrat.test.mjs`).
 */
export const REACTIONS_DE_MESSAGE = [
  { cle: "pouce", emoji: "\u{1F44D}" },
  { cle: "pouceBas", emoji: "\u{1F44E}" },
  { cle: "fete", emoji: "\u{1F389}" },
] as const;
/**
 * L'ANCIENNE CLE (P19 du 06/10/2026 : pouce, pouce bas, fete remplacent pouce, coeur, fete, « au
 * moins sans ambiguite ») : un coeur deja pose en base n'est ni efface ni
 * affiche. Le modele le TOLERE a la lecture et l'ecarte ; l'ecriture ne
 * l'accepte plus (`cleDeReaction`, rules : la liste reste telle quelle).
 */
export const CLE_DE_REACTION_RETIREE = "coeur";
export type CleDeReaction = (typeof REACTIONS_DE_MESSAGE)[number]["cle"];
export const CLES_DE_REACTION: readonly CleDeReaction[] = REACTIONS_DE_MESSAGE.map((r) => r.cle);
/** Personnes par emoji, au plus (rules : meme plafond). */
export const REACTIONS_PAR_EMOJI_MAX = 500;
const personnesQuiReagissent = z.array(z.string().min(1)).max(REACTIONS_PAR_EMOJI_MAX);
export const reactionsSchema = z
  .object({
    pouce: personnesQuiReagissent,
    pouceBas: personnesQuiReagissent,
    fete: personnesQuiReagissent,
    [CLE_DE_REACTION_RETIREE]: z.unknown(),
  })
  .partial()
  .strict()
  .transform(({ [CLE_DE_REACTION_RETIREE]: _retiree, ...actives }) => actives);
export type ReactionsDeMessage = z.output<typeof reactionsSchema>;
/** La cle d'un emoji de la liste fermee, `null` hors liste. */
export function cleDeReaction(emoji: string): CleDeReaction | null {
  return REACTIONS_DE_MESSAGE.find((r) => r.emoji === emoji || r.cle === emoji)?.cle ?? null;
}

/** La signature d'une reponse de Lia a une mention (`repondreMention`). */
export const signeLiaSchema = z
  .object({ mentionnePar: z.string().min(1), journalId: z.string().min(1).optional() })
  .strict();
export type SigneLia = z.infer<typeof signeLiaSchema>;

/**
 * Un evenement systeme dans une conversation (« champ modifie », « Lia
 * indisponible »). `journalId` : la ligne du journal d'audit quand l'evenement
 * en a une (un echec de Lia APRES l'appel au modele est journalise).
 */
export const systemeSchema = z
  .object({
    evenement: z.string().min(1).max(80),
    details: z.string().max(2000).optional(),
    journalId: z.string().min(1).optional(),
  })
  .strict();
export type EvenementSysteme = z.infer<typeof systemeSchema>;

/**
 * LES CHAMPS D'UN MESSAGE DE CONVERSATION (`type: 'conversation'`), de premier
 * niveau. `conversationId` (plus haut) y est obligatoire ; `parentId` dit le
 * fil ; `auteur` est un personId, ou `lia` / `systeme` (Functions seules) ;
 * `mentions` est DERIVE du texte par le SDK (`mentions.ts`).
 */
export const messageDeConversationFields = {
  /** Le message racine du fil. Absent : message racine. Immuable. */
  parentId: z.string().min(1).optional(),
  /** Le personId de l'auteur, ou `lia`, ou `systeme`. Immuable. */
  auteur: z.string().min(1).optional(),
  /** Le texte stocke, mentions en convention `@[personId]`, `@tous`, `@Lia`. */
  texte: z.string().min(1).max(MESSAGE_TEXTE_MAX).optional(),
  mentions: mentionsSchema.optional(),
  signeLia: signeLiaSchema.optional(),
  systeme: systemeSchema.optional(),
  /** Qui a reagi, par emoji. Ne s'ecrit QUE par `sdk.messages.reagir` / `retirerReaction`. */
  reactions: reactionsSchema.optional(),
} as const;

/** Champs métier de la famille message (hors champs standard du socle). */
export const messageCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "reminder", "confirmation", "reply"). */
  type: z.string().min(1),
  /** Canal de la communication, chaîne libre pilotée par l'app (ex. "email", "sms", "notification", "portal", "call"). Optionnel. */
  channel: z.string().min(1).optional(),
  /** Sens de la communication, état métier piloté par l'app (ex. "inbound", "outbound"). Optionnel. */
  direction: z.string().min(1).optional(),
  /** Émetteur : id polymorphe (contact, worker, org...) résolu par l'app selon le contexte. Optionnel. */
  fromRef: z.string().min(1).optional(),
  /** Destinataires : liste d'ids (contacts, workers...). Optionnel. */
  toRefs: z.array(z.string()).optional(),
  /** Objet / sujet du message. Optionnel (un sms ou une notification peut ne pas en avoir). */
  subject: z.string().min(1).optional(),
  /** Corps / contenu du message. Optionnel. */
  body: z.string().min(1).optional(),
  /** Date d'envoi (ou de réception). Optionnel. */
  sentAt: z.date().optional(),
  /** État de remise, chaîne libre pilotée par l'app (ex. "queued", "sent", "delivered", "read", "failed"). Optionnel. */
  deliveryStatus: z.string().min(1).optional(),
  /** Fil de conversation de rattachement : id polymorphe (thread). Optionnel. */
  threadRef: z.string().min(1).optional(),
  /**
   * La conversation (famille `conversations`) dont ce message herite l'acces.
   * Optionnel : un message sans conversation est equipe. Immuable.
   */
  conversationId: z.string().min(1).optional(),
  /** Contact concerné par le message (réf. famille contact). Optionnel. */
  contactId: z.string().min(1).optional(),
  /** Réservation / rendez-vous lié (réf. famille booking). Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Facture liée (réf. famille invoice). Optionnel. */
  invoiceId: z.string().min(1).optional(),
  /** Ticket de support lié (réf. famille ticket). Optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Campagne de rattachement (réf. famille campaign). Optionnel. */
  campaignId: z.string().min(1).optional(),
  /** Avis lié (réf. famille review). Optionnel. */
  reviewId: z.string().min(1).optional(),
  ...messageDeConversationFields,
} as const;

/**
 * Ce que `sdk.messages.envoyer` valide AVANT d'ecrire un message de
 * conversation : la conversation, le texte, le fil. L'auteur et les mentions
 * sont poses par le SDK, pas par l'appelant.
 */
export const MessageDeConversationInputSchema = z
  .object({
    conversationId: z.string().min(1),
    texte: z.string().trim().min(1).max(MESSAGE_TEXTE_MAX),
    parentId: z.string().min(1).optional(),
    mentions: mentionsSchema.partial().optional(),
  })
  .strict();
export type MessageDeConversationInput = z.infer<typeof MessageDeConversationInputSchema>;

/** Schéma complet de la famille message (champs standard + champs métier). */
export const MessageSchema = z.object({
  ...baseEntityFields,
  ...messageCoreFields,
  ...accesRefletField,
});

/** Type d'un message. */
export type Message = z.infer<typeof MessageSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const MessageCreateInputSchema = z
  .object({
    ...messageCoreFields,
    ...customFieldsField,
  })
  .omit({ reactions: true });

/** Type de l'entrée de création d'un message. */
export type MessageCreateInput = z.infer<typeof MessageCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId`, `type` et `conversationId` sont immuables (Security Rules) ; les
 * reactions passent par leurs verbes (`reagir`, `retirerReaction`) ; les
 * champs standard sont gérés par le SDK : aucun n'est accepté ici.
 */
export const MessageUpdateInputSchema = z
  .object({ ...messageCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true, conversationId: true, parentId: true, auteur: true, signeLia: true, systeme: true, reactions: true })
  .partial();

/** Type du patch de mise à jour d'un message. */
export type MessageUpdateInput = z.infer<typeof MessageUpdateInputSchema>;
